import config from 'constants/config'
import { CFMR_MAX_LEAD_TIME_DAYS } from 'constants/bookingProtection'
import { TRAVEL_PROTECTION_VALID_COUNTRIES } from 'constants/config/region'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { PROVIDER_COVER_GENIUS } from 'constants/insurance'
import memoize from 'lib/memoize/memoize'
import moment from 'moment'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'

// isCoverGeniusEnables checks if the Cover Genius should be used for
// an insurance provider for the given region code.
export const isCoverGeniusEnabled = memoize((regionCode: string): boolean => {
  return isEnabledForFeature(config.COVER_GENIUS_COUNTRIES, regionCode)
})

function isBookingProtectionCoverGeniusEnabled(regionCode: string): boolean {
  return isEnabledForFeature(config.INSURANCE_BOOKING_PROTECTION_ENABLED_REGIONS, regionCode)
}

/**
 * Whether CFMR (insurance booking protection) is sold to customers in `regionCode`.
 *
 * svc-insurance only quotes CFMR for the regions in `INSURANCE_BOOKING_PROTECTION_ENABLED_REGIONS`
 * and rejects every other origin, so any CFMR quote must be gated on this before it is requested.
 * Uses the CP region (geo.currentRegionCode), not destination countries.
 */
export function isInsuranceBookingProtectionEnabledForRegion(regionCode: string): boolean {
  return config.INSURANCE_BOOKING_PROTECTION_ENABLED && isBookingProtectionCoverGeniusEnabled(regionCode)
}

export function isInsuranceStartDateValid(startDate: string): boolean {
  const tomorrow = moment().startOf('day').add(1, 'day')
  return moment(startDate).isSameOrAfter(tomorrow)
}

export function isCoverGeniusEndDateValid(date: string | Date): boolean {
  const dateIn1Year = moment().startOf('day').add(1, 'year')
  return !moment(date).isSameOrAfter(dateIn1Year)
}

/**
 * Whether a trip starting on `startDate` (an ISO date) begins more than `CFMR_MAX_LEAD_TIME_DAYS` whole
 * days after today. An unknown start date never does.
 */
export function isBeyondCfmrLeadTime(startDate: string | undefined): boolean {
  if (!startDate) return false
  const daysUntilTripStart = moment(startDate, ISO_DATE_FORMAT).diff(moment().startOf('day'), 'days')
  return daysUntilTripStart > CFMR_MAX_LEAD_TIME_DAYS
}

export function getInsuranceProvider(regionCode: string, type?: App.InsuranceProductType) {
  const isCoverGenius = type === 'booking-protection' ?
      isBookingProtectionCoverGeniusEnabled(regionCode) : isCoverGeniusEnabled(regionCode)

  if (isCoverGenius) {
    return PROVIDER_COVER_GENIUS
  }

  return false
}

export function providerIsCoverGenius(regionCode: string, type?: App.InsuranceProductType) {
  return getInsuranceProvider(regionCode, type) === PROVIDER_COVER_GENIUS
}

/**
 * Whether travel-insurance quotes and products should use the mutual travel-protection
 * API (/api/insurance/travel-protection/*) vs legacy insurance (/api/insurance/*).
 *
 * Uses the CP region (geo.currentRegionCode), not destination countries.
 */
export function shouldUseTravelProtectionEndpoint(
  regionCode: string,
  productType: App.InsuranceProductType,
): boolean {
  return productType === 'travel-insurance' && TRAVEL_PROTECTION_VALID_COUNTRIES.includes(regionCode)
}

export function findInsuranceUpgradeSourceItem(item: App.OrderInsuranceItem, order: App.Order): App.OrderInsuranceItem | undefined {
  if (item?.upgradedFromId) {
    const upgradedFrom = order.insuranceItems.find(i => i.id === item.upgradedFromId)
    return upgradedFrom ? findInsuranceUpgradeSourceItem(upgradedFrom, order) : item
  }
  return item
}
